import { computed, inject, Injectable, OnDestroy, Signal } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { catchError, switchMap, take, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import {
  Contact,
  ContactExtended,
  ContactGroup,
  ContactGroupExtended,
  ContactPayload,
  ContactsStoreType,
} from '@app/store/contacts/contacts.types';
import { ContactsApiService } from '@app/services/contacts-api.service';
import { NotificationV2Service } from '@app/notification-v2.service';
import { stringToInitials } from '@app/utils/stringToIntials';
import { isTextPresentInValues } from '@app/utils/isTextPresentInValues';

@Injectable({ providedIn: 'root' })
export class ContactsStore implements OnDestroy {
  readonly notif = inject(NotificationV2Service);
  readonly store = inject(Store);
  readonly interactionBarStateService = inject(InteractionBarStateService);
  readonly contactApiService = inject(ContactsApiService);
  private isDestroyed$ = new Subject();

  private readonly state = signalState<ContactsStoreType>({
    isLoading: false,
    contactGroups: [],
    searchText: null,
  });

  public contactGroups: Signal<ContactGroupExtended[]> = computed(() => {
    const searchText = this.state.searchText();
    return this.state
      .contactGroups()
      .map((group): ContactGroupExtended => {
        const groupNameMatches = isTextPresentInValues(searchText, [group.name]);
        return {
          ...group,
          initials: stringToInitials(group.name),
          contacts: group.contacts
            .map((contact): ContactExtended => {
              return {
                ...contact,
                initials: (contact.first_name?.[0] ?? '') + (contact.last_name?.[0] ?? ''),
                formattedAddress: `${contact.address} ${contact.city}, ${contact.state} ${contact.zip}`,
              };
            })
            .filter((contact) => {
              const contactMatches = isTextPresentInValues(searchText, [
                `${contact.first_name} ${contact.last_name}`,
                contact.email,
                contact.title,
                contact.phone,
                contact.address,
                contact.city,
                contact.state,
                contact.zip,
              ]);
              return groupNameMatches || contactMatches;
            }),
        };
      })
      .filter((group) => {
        const groupNameMatches = isTextPresentInValues(searchText, [group.name]);
        return groupNameMatches || group.contacts.length > 0;
      });
  });

  public isLoading = this.state.isLoading;

  public searchText = this.state.searchText;

  contacts = computed(() =>
    this.contactGroups()
      .map((group) =>
        group.contacts.map((contact) => ({
          ...contact,
          company_name: group.name,
        })),
      )
      .flat(),
  );

  setLoading(isLoading: boolean) {
    patchState(this.state, { isLoading });
  }

  setSearchText(text: string) {
    patchState(this.state, { searchText: text });
  }

  loadContactGroups() {
    this.setLoading(true);
    this.contactApiService
      .getContactGroups()
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (groups) => {
          patchState(this.state, { contactGroups: groups, isLoading: false });
        },
        error: (err) => {
          console.warn('Failed to load contact groups', err);
          this.notif.showError('Failed to load contact groups');
          this.setLoading(false);
        },
      });
  }

  updateContact(contactPayload: ContactPayload, shouldCreateCompany: boolean) {
    if (!contactPayload.id) {
      throw new Error('Contact id is missing');
    }
    const hash = this.notif.showLoading();

    // default value just emits null
    let initialRequest = new BehaviorSubject(null).asObservable().pipe(take(1));
    if (shouldCreateCompany) {
      // if we need to create a new company, first create it, then use the returned id to update the contact
      initialRequest = this.contactApiService
        .createContactGroup({ name: contactPayload.new_company_name })
        .pipe(takeUntil(this.isDestroyed$));
    }

    initialRequest
      .pipe(
        switchMap((group) => {
          if (group && 'id' in group) {
            contactPayload.group_id = group.id;
          }
          return this.contactApiService.updateContact(contactPayload.id, contactPayload).pipe(
            takeUntil(this.isDestroyed$),
            catchError((error) => {
              this.notif.close(hash);
              this.notif.showError('Failed to create contact group');
              throw error;
            }),
          );
        }),
      )
      .subscribe({
        next: (contact) => {
          this.notif.close(hash);
          this.notif.showSuccess('Contact updated successfully');
          this.loadContactGroups();
          this.interactionBarStateService.close();
        },
        error: (err) => {
          console.warn('Failed to update contact', err);
          this.notif.close(hash);
          this.notif.showError('Failed to update contact');
        },
      });
  }

  createContact(contactPayload: ContactPayload, shouldCreateCompany: boolean) {
    const hash = this.notif.showLoading();

    // default value just emits null
    let initialRequest = new BehaviorSubject(null).asObservable().pipe(take(1));
    if (shouldCreateCompany) {
      // if we need to create a new company, first create it, then use the returned id to update the contact
      initialRequest = this.contactApiService
        .createContactGroup({ name: contactPayload.new_company_name })
        .pipe(
          takeUntil(this.isDestroyed$),
          catchError((error) => {
            this.notif.close(hash);
            this.notif.showError('Failed to create contact group');
            throw error;
          }),
        );
    }

    initialRequest
      .pipe(
        switchMap((group) => {
          if (group && 'id' in group) {
            contactPayload.group_id = group.id;
          }
          return this.contactApiService
            .createContact(contactPayload)
            .pipe(takeUntil(this.isDestroyed$));
        }),
      )
      .subscribe({
        next: (contact) => {
          this.notif.close(hash);
          this.notif.showSuccess('Contact created successfully');
          this.loadContactGroups();
          this.interactionBarStateService.close();
        },
        error: (err) => {
          console.warn('Failed to create contact', err);
          this.notif.close(hash);
          this.notif.showError('Failed to create contact');
        },
      });
  }

  renameContactGroup(id: number, groupName: string) {
    const hash = this.notif.showLoading();
    this.contactApiService
      .updateContactGroup(id, { name: groupName })
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (group) => {
          this.notif.close(hash);
          this.notif.showSuccess('Group renamed successfully');
          this.loadContactGroups();
        },
        error: (err) => {
          console.warn('Failed to rename group', err);
          this.notif.close(hash);
          this.notif.showError('Failed to rename group');
        },
      });
  }

  deleteContactGroup(contactGroup: ContactGroup) {
    const hash = this.notif.showLoading();
    this.contactApiService
      .deleteContactGroup(contactGroup.id)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (res) => {
          this.notif.close(hash);
          this.notif.showSuccess('Group deleted successfully');
          this.loadContactGroups();
        },
        error: (err) => {
          console.warn('Failed to delete group', err);
          this.notif.close(hash);
          this.notif.showError('Failed to delete group');
        },
      });
  }

  deleteContact(contact: Contact) {
    const hash = this.notif.showLoading();
    this.contactApiService
      .deleteContact(contact.id)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (res) => {
          this.notif.close(hash);
          this.notif.showSuccess('Contact deleted successfully.');
          this.loadContactGroups();
        },
        error: (err) => {
          console.warn('Failed to delete contact', err);
          this.notif.close(hash);
          this.notif.showError('Failed to delete contact.');
        },
      });
  }
  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }
}
